import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Volume2, VolumeX } from "lucide-react";
import CinematicWorldConnections from "../globe/CinematicWorldConnections";
import { useIsMobile } from "@/hooks/use-mobile";

gsap.registerPlugin(ScrollTrigger);

export default function CinematicStory() {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isMuted, setIsMuted] = useState(true);
  const isMobile = useIsMobile();

  useEffect(() => {
    const ctx = gsap.context(() => {
      // Setup particles canvas animation
      const setupParticles = () => {
        const canvas = document.querySelector("#story-particles") as HTMLCanvasElement;
        if (!canvas) return;

        const context = canvas.getContext("2d");
        if (!context) return;

        canvas.width = window.innerWidth;
        canvas.height = window.innerHeight;

        const particles = Array.from({ length: 80 }, () => ({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          r: Math.random() * 1.5 + 0.3,
          dx: (Math.random() - 0.5) * 0.3,
          dy: (Math.random() - 0.5) * 0.3,
          opacity: Math.random() * 0.4 + 0.2,
        }));

        function animate() {
          if (!context || !canvas) return;
          context.clearRect(0, 0, canvas.width, canvas.height);

          particles.forEach((p) => {
            // Particle with soft glow
            context.fillStyle = `rgba(216, 150, 248, ${p.opacity})`;
            context.beginPath();
            context.arc(p.x, p.y, p.r, 0, Math.PI * 2);
            context.fill();

            // Glow effect
            const grad = context.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 4);
            grad.addColorStop(0, `rgba(245, 211, 123, ${p.opacity * 0.2})`);
            grad.addColorStop(1, "rgba(216, 150, 248, 0)");
            context.fillStyle = grad;
            context.beginPath();
            context.arc(p.x, p.y, p.r * 4, 0, Math.PI * 2);
            context.fill();

            p.x += p.dx;
            p.y += p.dy;
            if (p.x < 0 || p.x > canvas.width) p.dx *= -1;
            if (p.y < 0 || p.y > canvas.height) p.dy *= -1;
          });

          requestAnimationFrame(animate);
        }

        animate();

        const handleResize = () => {
          canvas.width = window.innerWidth;
          canvas.height = window.innerHeight;
        };
        window.addEventListener("resize", handleResize);

        return () => window.removeEventListener("resize", handleResize);
      };

      setupParticles();

      // Initial hero animation (not scroll-driven)
      gsap.fromTo(
        ".hero-title",
        { opacity: 0, y: 60, scale: 0.9 },
        { opacity: 1, y: 0, scale: 1, duration: 2, ease: "power3.out", delay: 0.5 },
      );

      gsap.fromTo(
        ".hero-subtitle",
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 1.5, ease: "power3.out", delay: 1.2 },
      );

      // DIOR-STYLE: Hero shifts left, Two Worlds reveals from right
      const heroTransition = gsap.timeline({
        scrollTrigger: {
          trigger: ".hero-wrapper",
          start: "top top",
          end: "+=100vh",
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        },
      });

      heroTransition
        .to(".hero-content", {
          x: "-40vw",
          opacity: 0.3,
          scale: 0.8,
          ease: "power2.inOut",
        })
        .to(
          ".two-worlds-reveal",
          {
            x: "0%",
            opacity: 1,
            ease: "power2.inOut",
          },
          0,
        );

      // Section 2: Two Worlds - Split screen merge (SCRUBBED)
      const twoWorldsTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: ".two-worlds-section",
          start: "top top",
          end: "+=200vh",
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        },
      });

      twoWorldsTimeline
        .fromTo(".photo-left", { x: "-100%", opacity: 0 }, { x: "0%", opacity: 1, ease: "power3.out" })
        .fromTo(".photo-right", { x: "100%", opacity: 0 }, { x: "0%", opacity: 1, ease: "power3.out" }, 0)
        .fromTo(".world-text-left", { opacity: 0, x: -50 }, { opacity: 1, x: 0, ease: "power3.out" }, 0.3)
        .fromTo(".world-text-right", { opacity: 0, x: 50 }, { opacity: 1, x: 0, ease: "power3.out" }, 0.3)
        .to(
          [".photo-left", ".photo-right", ".world-text-left", ".world-text-right"],
          { opacity: 0, scale: 0.95, ease: "power2.inOut" },
          0.6,
        )
        .fromTo(
          ".photo-together",
          { opacity: 0, scale: 1.05, filter: "blur(10px)" },
          { opacity: 1, scale: 1, filter: "blur(0px)", ease: "power3.out" },
          0.8,
        );

      // Section 3: The Encounter - Parallax zoom (SCRUBBED)
      const encounterTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: ".encounter-section",
          start: "top top",
          end: "+=150vh",
          pin: true,
          scrub: 1,
          anticipatePin: 1,
        },
      });

      encounterTimeline
        .fromTo(".encounter-scene", { scale: 1.3, opacity: 0 }, { scale: 1, opacity: 1, ease: "power3.out" })
        .fromTo(
          ".encounter-word",
          { opacity: 0, y: 40, scale: 0.9 },
          {
            opacity: 1,
            y: 0,
            scale: 1,
            stagger: 0.2,
            ease: "power3.out",
          },
          0.3,
        );

      // Section 4: The Journey - now uses 3D globe component with its own animations

      // Section 5: The Legacy (SCRUBBED)
      const legacyTimeline = gsap.timeline({
        scrollTrigger: {
          trigger: ".legacy-section",
          start: "top center",
          end: "bottom center",
          scrub: 1,
        },
      });

      legacyTimeline
        .fromTo(
          ".legacy-counter",
          { innerText: 0 },
          {
            innerText: 10000,
            duration: 1,
            snap: { innerText: 1000 },
            onUpdate: function () {
              const el = document.querySelector(".legacy-counter");
              if (el) el.textContent = Math.round(this.targets()[0].innerText).toLocaleString() + "+";
            },
          },
        )
        .fromTo(
          ".student-portrait",
          { opacity: 0, scale: 0 },
          { opacity: 1, scale: 1, stagger: 0.02, ease: "back.out(1.7)" },
          0.5,
        );

      // Section 6: The Essence (SCRUBBED)
      gsap.fromTo(
        ".essence-word",
        { opacity: 0, y: 50 },
        {
          opacity: 1,
          y: 0,
          stagger: 0.3,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".essence-section",
            start: "top center",
            end: "center center",
            scrub: 1,
          },
        },
      );

      gsap.fromTo(
        ".essence-logo",
        { opacity: 0, scale: 0.8 },
        {
          opacity: 1,
          scale: 1,
          ease: "power3.out",
          scrollTrigger: {
            trigger: ".essence-section",
            start: "center center",
            end: "bottom center",
            scrub: 1,
          },
        },
      );
    }, containerRef);

    return () => ctx.revert();
  }, []);

  const toggleMute = () => setIsMuted((prev) => !prev);

  return (
    <div ref={containerRef} className="relative bg-black overflow-x-hidden">
      {/* Particle canvas */}
      <canvas id="story-particles" className="fixed inset-0 z-0 pointer-events-none" />

      {/* Audio Toggle */}
      <button
        onClick={toggleMute}
        className="fixed top-8 right-8 z-50 p-3 rounded-full bg-[hsl(var(--story-deep-purple))]/80 backdrop-blur-sm border border-[hsl(var(--story-soft-pink))]/20 hover:border-[hsl(var(--story-soft-pink))]/40 transition-all"
        aria-label="Toggle audio"
      >
        {isMuted ? (
          <VolumeX className="w-5 h-5 text-[hsl(var(--story-off-white))]" />
        ) : (
          <Volume2 className="w-5 h-5 text-[hsl(var(--story-soft-pink))]" />
        )}
      </button>

      {/* Section 1: Hero with Dior-style transition */}
      <div className="hero-wrapper relative h-screen w-full overflow-hidden bg-[hsl(var(--story-deep-purple))]">
        {/* Hero Content - will slide left */}
        <div className="absolute inset-0 w-full h-full">
          <section className="hero-section w-full h-full flex items-center justify-center">
            <div className="hero-content relative z-20 text-center px-8 max-w-7xl mx-auto">
              {/* Background */}
              <div className="absolute inset-0 -z-10 left-1/2 -translate-x-1/2 w-screen">
                {/* NOTE: Image should be optimized to ~1200x800px (2x retina: 2400x1600px) for hero background */}
                <img
                  src="/assets/together.jpg"
                  alt="Klau and Ros dancing"
                  className="w-full h-full object-cover opacity-40"
                  loading="lazy"
                  width={1200}
                  height={800}
                />
                {!isMobile && (
                  <div className="absolute inset-0 bg-gradient-to-b from-[hsl(var(--story-deep-purple))]/60 via-[hsl(var(--story-deep-purple))]/40 to-[hsl(var(--story-deep-purple))]" />
                )}
              </div>

              <h1 className="hero-title font-serif text-7xl md:text-9xl font-bold text-[hsl(var(--story-off-white))] mb-6 tracking-tight">
                Klau & Ros
              </h1>
              <p className="hero-subtitle font-serif text-2xl md:text-4xl text-[hsl(var(--story-gold))] italic">
                An Endless Story of Movement & Connection
              </p>

              {/* Gradient lighting effect */}
              <div className="absolute bottom-0 left-1/2 -translate-x-1/2 w-[600px] h-[400px] bg-[hsl(var(--story-soft-pink))]/20 blur-[120px] rounded-full pointer-events-none" />
            </div>
          </section>
        </div>

        {/* Two Worlds reveal from right - slides in OVER hero */}
        <div className="two-worlds-reveal absolute inset-0 w-full h-full translate-x-full opacity-0 bg-[hsl(var(--story-deep-purple))] flex items-center justify-center z-30">
          <p className="font-serif text-4xl md:text-6xl text-[hsl(var(--story-soft-pink))] italic text-center px-8 max-w-3xl">
            Two worlds. One destiny.
          </p>
        </div>
      </div>

      {/* Section 2: Two Worlds */}
      <section className="two-worlds-section relative w-full min-h-screen flex items-center justify-center bg-black overflow-hidden">
        {/* Subtle parallax background - Disabled on mobile */}
        {!isMobile && (
          <div className="absolute inset-0 z-0">
            <div className="absolute inset-0 bg-[hsl(var(--story-deep-purple))]" />
            <div className="absolute inset-0 bg-gradient-to-br from-[hsl(var(--story-deep-purple))] via-black/60 to-[hsl(var(--story-soft-pink))]/10" />
            <div className="absolute inset-0 backdrop-blur-[2px]" />
          </div>
        )}

        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8 z-10">
          {/* Split worlds */}
          <div className="relative flex flex-col md:flex-row h-[70vh] w-full overflow-hidden rounded-2xl shadow-[0_0_80px_rgba(255,255,255,0.05)]">
            {/* Left world */}
            <div className="photo-left relative flex-1 overflow-hidden">
              {/* NOTE: Image should be optimized to ~1200x800px (2x retina: 2400x1600px) for h-[70vh] display */}
              <img
                src="/assets/klau-left.jpg"
                alt="Klau from Poland"
                className="w-full h-full object-cover scale-105"
                loading="lazy"
                width={1200}
                height={800}
              />
              {!isMobile && (
                <div className="absolute inset-0 bg-gradient-to-r from-[hsl(var(--story-deep-purple))]/80 via-transparent to-transparent" />
              )}
              <div className="absolute bottom-8 left-8 world-text-left font-serif text-xl md:text-3xl text-[hsl(var(--story-off-white))] italic max-w-xs">
                Where winter's discipline shaped grace.
              </div>
            </div>

            {/* Divider */}
            <div className="absolute top-0 left-1/2 -translate-x-1/2 h-full w-px bg-gradient-to-b from-[hsl(var(--story-gold))]/40 via-[hsl(var(--story-soft-pink))]/20 to-transparent blur-[1px]" />

            {/* Right world */}
            <div className="photo-right relative flex-1 overflow-hidden">
              {/* NOTE: Image should be optimized to ~1200x800px (2x retina: 2400x1600px) for h-[70vh] display */}
              <img
                src="/assets/ros-right.jpg"
                alt="Ros from Spain"
                className="w-full h-full object-cover scale-105"
                loading="lazy"
                width={1200}
                height={800}
              />
              {!isMobile && (
                <div className="absolute inset-0 bg-gradient-to-l from-[hsl(var(--story-deep-purple))]/80 via-transparent to-transparent" />
              )}
              <div className="absolute bottom-8 right-8 world-text-right font-serif text-xl md:text-3xl text-[hsl(var(--story-off-white))] italic text-right max-w-xs">
                Where sun-soaked rhythm gave life to passion.
              </div>
            </div>

            {/* Merge world photo (revealed on scroll) */}
            <div className="photo-together absolute inset-0 opacity-0 z-20">
              {/* NOTE: Image should be optimized to ~1200x800px (2x retina: 2400x1600px) for h-[70vh] display */}
              <img
                src="/assets/together.jpg"
                alt="Klau and Ros together"
                className="w-full h-full object-cover"
                loading="lazy"
                width={1200}
                height={800}
              />
              {!isMobile && (
                <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-[hsl(var(--story-deep-purple))]/30 to-black/60" />
              )}
            </div>
          </div>

          <p className="mt-20 text-center font-serif text-2xl md:text-4xl text-[hsl(var(--story-soft-pink))] italic">
          Two journeys converging in the language of movement.
          </p>
        </div>
      </section>

      {/* Section 3: The Encounter */}
      <section className="encounter-section relative w-full min-h-screen flex items-center justify-center bg-gradient-to-b from-[hsl(var(--story-deep-purple))] to-black overflow-hidden py-32">
        {/* Background scene */}
        <div className="encounter-scene absolute inset-0 z-0 opacity-30">
          <div className="absolute inset-0 bg-gradient-radial from-[hsl(var(--story-soft-pink))]/10 via-transparent to-transparent" />
        </div>

        <div className="relative z-10 text-center px-8 space-y-12 max-w-7xl mx-auto">
          <div className="encounter-word font-serif text-4xl md:text-7xl text-[hsl(var(--story-off-white))]">
            One look.
          </div>
          <div className="encounter-word font-serif text-4xl md:text-7xl text-[hsl(var(--story-gold))]">One song.</div>
          <div className="encounter-word font-serif text-4xl md:text-7xl text-[hsl(var(--story-soft-pink))] relative inline-block">
            One rhythm.
            <div className="absolute inset-0 blur-xl bg-[hsl(var(--story-soft-pink))]/30 animate-pulse" />
          </div>
        </div>
      </section>

      {/* Section 4: The Journey */}
      <section className="journey-section relative w-full min-h-screen flex items-center justify-center bg-black py-32 overflow-hidden">
        <div className="relative w-full max-w-7xl mx-auto px-4 md:px-8">
          <h2 className="font-serif text-3xl md:text-6xl text-center text-[hsl(var(--story-off-white))] mb-20">
            The Journey
          </h2>

          {/* World Map Visualization */}
          <div className="relative w-full h-[600px] md:h-[700px] flex items-center justify-center bg-gradient-to-b from-[#0b001a] to-[#2a0a3f] rounded-2xl overflow-hidden">
            {/* Glow effect background */}
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_hsl(var(--story-soft-pink))/10,_transparent_70%)]" />
            
            {/* Animated world map with connections */}
            <div className="relative w-full h-full flex items-center justify-center">
              {/* City nodes with connections */}
              <svg className="absolute inset-0 w-full h-full" viewBox="0 0 1000 600" preserveAspectRatio="xMidYMid meet">
                {/* Connection lines */}
                <g className="connections" opacity="0.6">
                  {/* Europe connections */}
                  <path className="journey-card" d="M 250,280 Q 280,260 310,275" stroke="url(#lineGradient)" strokeWidth="2" fill="none" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" from="100" to="0" dur="3s" repeatCount="indefinite" />
                  </path>
                  <path className="journey-card" d="M 310,275 Q 340,270 370,280" stroke="url(#lineGradient)" strokeWidth="2" fill="none" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" from="100" to="0" dur="3s" begin="0.5s" repeatCount="indefinite" />
                  </path>
                  
                  {/* Europe to Asia */}
                  <path className="journey-card" d="M 370,280 Q 500,250 630,320" stroke="url(#lineGradient)" strokeWidth="2" fill="none" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" from="200" to="0" dur="4s" begin="1s" repeatCount="indefinite" />
                  </path>
                  
                  {/* Asia connections */}
                  <path className="journey-card" d="M 630,320 Q 680,310 730,325" stroke="url(#lineGradient)" strokeWidth="2" fill="none" strokeDasharray="5,5">
                    <animate attributeName="stroke-dashoffset" from="100" to="0" dur="3s" begin="1.5s" repeatCount="indefinite" />
                  </path>
                  
                  {/* Asia to Americas */}
                  <path className="journey-card" d="M 730,325 Q 600,200 200,300" stroke="url(#lineGradient)" strokeWidth="2" fill="none" strokeDasharray="5,5" opacity="0.4">
                    <animate attributeName="stroke-dashoffset" from="300" to="0" dur="5s" begin="2s" repeatCount="indefinite" />
                  </path>
                </g>
                
                <defs>
                  <linearGradient id="lineGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="hsl(var(--story-soft-pink))" stopOpacity="0.8" />
                    <stop offset="50%" stopColor="hsl(var(--story-gold))" stopOpacity="1" />
                    <stop offset="100%" stopColor="hsl(var(--story-soft-pink))" stopOpacity="0.8" />
                  </linearGradient>
                </defs>
              </svg>
              
              {/* City markers */}
              <div className="absolute inset-0">
                {[
                  { name: "Madrid", x: "25%", y: "47%" },
                  { name: "Barcelona", x: "31%", y: "46%" },
                  { name: "Paris", x: "37%", y: "47%" },
                  { name: "Singapore", x: "63%", y: "53%" },
                  { name: "Seoul", x: "68%", y: "48%" },
                  { name: "Tokyo", x: "73%", y: "54%" },
                  { name: "New York", x: "20%", y: "50%" },
                  { name: "Brazil", x: "30%", y: "65%" },
                ].map((city, i) => (
                  <div
                    key={city.name}
                    className="journey-card absolute transform -translate-x-1/2 -translate-y-1/2"
                    style={{ left: city.x, top: city.y }}
                  >
                    {/* Pulsing outer ring */}
                    <div className="absolute inset-0 w-8 h-8 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2">
                      <div className="w-full h-full rounded-full border-2 border-[hsl(var(--story-gold))]/30 animate-ping" />
                    </div>
                    
                    {/* Dot */}
                    <div className="w-3 h-3 bg-[hsl(var(--story-gold))] rounded-full shadow-[0_0_20px_hsl(var(--story-gold))]" />
                    
                    {/* Label */}
                    <p className="absolute top-6 left-1/2 -translate-x-1/2 text-[hsl(var(--story-off-white))] text-xs whitespace-nowrap opacity-70">
                      {city.name}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <p className="text-center font-serif text-xl md:text-3xl text-[hsl(var(--story-soft-pink))] italic max-w-3xl mx-auto mt-20">
            Their art crossed continents, inspiring thousands to move with emotion.
          </p>
        </div>
      </section>

      {/* Section 5: The Legacy */}
      <section className="legacy-section relative w-full min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-black to-[hsl(var(--story-deep-purple))] py-32 overflow-hidden">
        <div className="relative z-10 text-center px-8 mb-20">
          <div className="legacy-counter font-serif text-6xl md:text-9xl text-[hsl(var(--story-gold))] font-bold mb-8">
            0+
          </div>
          <p className="font-serif text-xl md:text-3xl text-[hsl(var(--story-off-white))] italic">
            hearts moved worldwide
          </p>
        </div>

        {/* Constellation of student portraits */}
        <div className="relative w-full max-w-6xl mx-auto px-4 md:px-8">
          <div className="grid grid-cols-4 md:grid-cols-8 gap-3 md:gap-4 justify-items-center">
            {Array.from({ length: 32 }).map((_, i) => (
              <div
                key={i}
                className="student-portrait w-10 h-10 md:w-12 md:h-12 rounded-full bg-[hsl(var(--story-soft-pink))]/20 backdrop-blur-sm"
              />
            ))}
          </div>
        </div>

        <p className="text-center font-serif text-lg md:text-2xl text-[hsl(var(--story-off-white))]/80 italic max-w-3xl mx-auto mt-20 px-8">
          Through the Endless Bachata Academy, they continue shaping the next generation.
        </p>
      </section>

      {/* Section 6: The Essence */}
      <section className="essence-section relative w-full min-h-screen flex flex-col items-center justify-center bg-black py-32 overflow-hidden">
        {/* Soft spotlight */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-[hsl(var(--story-soft-pink))]/10 blur-[150px] rounded-full pointer-events-none" />

        <div className="relative z-10 text-center px-8 space-y-12 mb-20 max-w-7xl mx-auto">
          <div className="space-y-6">
            <p className="essence-word font-serif text-3xl md:text-6xl text-[hsl(var(--story-off-white))]">
              Every step.
            </p>
            <p className="essence-word font-serif text-3xl md:text-6xl text-[hsl(var(--story-off-white))]">
              Every look.
            </p>
            <p className="essence-word font-serif text-3xl md:text-6xl text-[hsl(var(--story-off-white))]">
              Every heartbeat.
            </p>
          </div>

          <div className="essence-word font-serif text-6xl md:text-9xl text-[hsl(var(--story-gold))] font-bold mt-16 relative inline-block">
            Endless.
            <div className="absolute inset-0 blur-2xl bg-[hsl(var(--story-gold))]/20 pointer-events-none" />
          </div>
        </div>

        {/* Logo lockup */}
        <div className="essence-logo">
          <img src="/assets/kr-logo.png" alt="Klau y Ros" className="w-24 h-24 md:w-32 md:h-32 opacity-60" />
        </div>
      </section>
    </div>
  );
}
